import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { scroller } from 'react-scroll';
import Localize from 'components/Localize';
import { connect } from 'react-redux';
import { setSelectedInternship } from 'actions/selectedInternshipActions';
import { getInternship } from 'actions/internshipsActions';
import Can from 'components/Can';
import { actions } from 'utils/access-rules/constants';
import { section } from 'utils/access-rules/rules/sections';
import InternshipSwitcher from 'components/InternshipSwitcher';
import InternshipInfo from '../../ApplicantProfile/InternshipInfo';
import { formatInternshipLabel } from '../../ApplicantProfile/internshipOrder';

// Scroll-Anchor des Anfrage-Bereichs in PrimaryUserData (Settings-Seite). Konsistent
// zum bestehenden Deeplink-Muster (#your-practices + scroller.scrollTo) der App.
export const REQUEST_SECTION_ANCHOR = 'request-new-internship';

export const InternshipSwitcherContainer = ({
  isAuthenticated,
  internships,
  selectedInternshipId,
  isSelectedInternshipLoaded,
  userRole,
  history,
  setSelectedInternship,
  getInternship,
}) => {
  // 🔴 Dieser Switcher ist der ZWEITE Eingang zu derselben Auswahl: Er sieht aus wie der
  // im Bewerberprofil und traegt dieselbe „Kein Praktikum"-Zeile, hat den Zeitraum aus
  // 2-C aber nicht geerbt (Fehlerklasse BF). Beide benutzen jetzt dieselbe Beschriftung
  // aus internshipOrder — laege sie zweimal vor, liefen die Anzeigen wieder auseinander.
  // Die Daten sind vorhanden: /internship/esuser/ liefert starts_at und ends_at mit.
  const options = internships.map((internship) => ({
    value: internship.pk,
    label: formatInternshipLabel(internship),
  }));
  const onInternshipChange = ({ value }) => {
    const pathArray = history.location.pathname.split('/');
    const currentPath = pathArray[1];
    const internship = internships.find(({ pk }) => pk === value);
    if (!internship) {
      setSelectedInternship({ pk: null });
      history.push(`/${currentPath}/`);
    } else {
      setSelectedInternship(internship);
      history.push(`/${currentPath}/${value}`);
    }
  };
  useEffect(() => {
    const pathArray = history.location.pathname.split('/');
    if (
      internships.length > 0
      && pathArray[pathArray.length - 1] === ':internshipId'
    ) {
      onInternshipChange(options[0]);
    }
  }, [history.location.pathname]);
  // Der globale Header-Switcher zeigt via InternshipInfo u.a. die BuB
  // (education_providers). Die stehen nur im vollen Internship-Objekt
  // (Retrieve-Endpoint, state.internships.byId), nicht in currentUser.internships.
  // Ohne diesen Fetch bliebe die BuB-Zeile im Header leer, solange keine Detailseite
  // besucht wurde. Guard: nur laden, wenn das Praktikum noch nicht im Store liegt.
  useEffect(() => {
    if (selectedInternshipId && !isSelectedInternshipLoaded) {
      getInternship(selectedInternshipId);
    }
  }, [selectedInternshipId, isSelectedInternshipLoaded, getInternship]);
  // Begleit-CTA: leitet zum bestehenden "Neues Praktikum beim Lehrer anfragen"-
  // Einstieg (InternshipsRequestsList auf der Settings-Seite) und scrollt dorthin.
  // Bewusst KEIN blindes addInternshipRequest()-Dispatch: der Bestand legt den
  // leeren Request IM Kontext der sichtbaren Liste an; ein Dispatch vom Switcher
  // aus wuerde einen Datensatz ausserhalb dieses Kontexts erzeugen.
  const onRequestNewInternship = () => {
    const alreadyOnSettings = history.location.pathname.startsWith('/settings');
    if (!alreadyOnSettings) {
      history.push('/settings');
    }
    // The request list (and thus the scroll anchor) mounts only AFTER its data
    // has loaded async, so a fixed delay races the API and the scroll silently
    // no-ops on a slow connection. Poll for the anchor and scroll once it exists;
    // give up after ~5s so this never loops forever (fail-safe, no scroll then).
    let attempts = 0;
    const maxAttempts = 50;
    const tryScroll = () => {
      if (scroller.get(REQUEST_SECTION_ANCHOR)) {
        scroller.scrollTo(REQUEST_SECTION_ANCHOR, {
          duration: 800,
          delay: 0,
          smooth: 'easeInOutQuart',
        });
      } else if (attempts < maxAttempts) {
        attempts += 1;
        setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
  };
  if (!isAuthenticated) {
    return null;
  }
  return (
    <Can I={actions.see} a={section.InternshipSwitcher}>
      <Localize>
        {({ translate }) => (
          <InternshipSwitcher
            options={[
              { value: null, label: translate('no-internship') },
              ...options,
            ]}
            onChange={onInternshipChange}
            selectedInternshipId={selectedInternshipId}
            userRole={userRole}
            onRequestNewInternship={onRequestNewInternship}
          />
        )}
      </Localize>
      <InternshipInfo
        selectedInternshipId={selectedInternshipId}
        margin="0 auto"
        padding="10px 10px 0"
      />
    </Can>
  );
};

InternshipSwitcherContainer.propTypes = {
  internships: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedInternshipId: PropTypes.number,
  isAuthenticated: PropTypes.bool.isRequired,
  userRole: PropTypes.string.isRequired,
  setSelectedInternship: PropTypes.func.isRequired,
  getInternship: PropTypes.func.isRequired,
  isSelectedInternshipLoaded: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

InternshipSwitcherContainer.defaultProps = {
  selectedInternshipId: null,
  isSelectedInternshipLoaded: false,
};

export const mapStateToProps = ({ currentUser, selectedInternship, internships }) => ({
  isAuthenticated: !!currentUser.data,
  userRole: currentUser.data && currentUser.data.role,
  internships: (currentUser.data && currentUser.data.internships) || [],
  selectedInternshipId: selectedInternship && selectedInternship.pk,
  isSelectedInternshipLoaded: !!(
    selectedInternship
    && selectedInternship.pk
    && internships
    && internships.data
    && internships.data.byId
    && internships.data.byId[selectedInternship.pk]
  ),
});

export default connect(
  mapStateToProps,
  { setSelectedInternship, getInternship },
)(withRouter(InternshipSwitcherContainer));
